import { type FormEvent, useEffect, useMemo, useState } from "react";
import type { WorkspaceSnapshot } from "./lib/adminWorkspaceRepository";
import {
  DEFAULT_PROPOSAL_PORTRAIT,
  defaultProposalExpiry,
  localDateTimeToIso,
  proposalInvitationMailto,
} from "./lib/proposalAdminModel";
import {
  createPersonalProposal,
  type CreatedPersonalProposal,
} from "./lib/proposalAdminRepository";
import "./personal-proposal-admin.css";

const TIMEZONE = "Europe/Berlin";
let optionCounter = 0;

type Props = {
  snapshot: WorkspaceSnapshot;
};

type ProposalTimeDraft = {
  id: string;
  value: string;
};

function createTimeDraft(): ProposalTimeDraft {
  optionCounter += 1;
  return { id: `proposal-time-${Date.now()}-${optionCounter}`, value: "" };
}

export function PersonalProposalAdmin({ snapshot }: Props) {
  const activeOffers = useMemo(
    () =>
      snapshot.offers.filter(
        (offer) => offer.isActive && offer.variants.some((variant) => variant.isActive),
      ),
    [snapshot.offers],
  );
  const [offerId, setOfferId] = useState(activeOffers[0]?.id ?? "");
  const selectedOffer = activeOffers.find((offer) => offer.id === offerId) ?? activeOffers[0];
  const activeVariants = selectedOffer?.variants.filter((variant) => variant.isActive) ?? [];
  const [variantId, setVariantId] = useState(activeVariants[0]?.id ?? "");
  const selectedVariant =
    activeVariants.find((variant) => variant.id === variantId) ?? activeVariants[0];
  const activeLocationIds = useMemo(
    () =>
      new Set(
        snapshot.offerLocations
          .filter((link) => link.offerId === selectedOffer?.id && link.isActive)
          .map((link) => link.locationId),
      ),
    [selectedOffer?.id, snapshot.offerLocations],
  );
  const availableLocations = snapshot.locations.filter(
    (location) => location.isActive && activeLocationIds.has(location.id),
  );
  const [locationId, setLocationId] = useState(availableLocations[0]?.id ?? "");

  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [displayPrice, setDisplayPrice] = useState("");
  const [imageUrl, setImageUrl] = useState(DEFAULT_PROPOSAL_PORTRAIT);
  const [expiresAt, setExpiresAt] = useState(() => defaultProposalExpiry());
  const [options, setOptions] = useState<ProposalTimeDraft[]>(() => [createTimeDraft()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatedPersonalProposal | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!selectedOffer && activeOffers[0]) setOfferId(activeOffers[0].id);
  }, [activeOffers, selectedOffer]);

  useEffect(() => {
    if (!activeVariants.some((variant) => variant.id === variantId)) {
      setVariantId(activeVariants[0]?.id ?? "");
    }
  }, [activeVariants, variantId]);

  useEffect(() => {
    if (!availableLocations.some((location) => location.id === locationId)) {
      setLocationId(availableLocations[0]?.id ?? "");
    }
  }, [availableLocations, locationId]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setCopied(false);

    try {
      if (!selectedOffer || !selectedVariant || !locationId) {
        throw new Error("Bitte Angebot, Dauer und Ort vollständig auswählen.");
      }
      const selectedTimes = options.map((option) => option.value).filter(Boolean);
      if (selectedTimes.length === 0 || selectedTimes.length !== options.length) {
        throw new Error(
          "Bitte jede vorgeschlagene Zeit vollständig auswählen oder leere Zeilen entfernen.",
        );
      }
      const startsAt = selectedTimes.map((value) => localDateTimeToIso(value, TIMEZONE));
      if (new Set(startsAt).size !== startsAt.length) {
        throw new Error("Eine vorgeschlagene Zeit wurde doppelt eingetragen.");
      }

      const price = displayPrice.trim();
      const priceCents =
        price === "" ? undefined : Math.round(Number(price.replace(",", ".")) * 100);
      if (priceCents !== undefined && (!Number.isFinite(priceCents) || priceCents < 0)) {
        throw new Error("Bitte einen gültigen persönlichen Preis eintragen.");
      }

      setBusy(true);
      const created = await createPersonalProposal({
        recipientName: recipientName.trim(),
        recipientEmail: recipientEmail.trim() || undefined,
        message: message.trim(),
        offerId: selectedOffer.id,
        variantId: selectedVariant.id,
        locationId,
        displayPriceCents: priceCents,
        currency: selectedVariant.currency || "EUR",
        imageUrl: imageUrl.trim() || undefined,
        expiresAt: expiresAt ? localDateTimeToIso(expiresAt, TIMEZONE) : undefined,
        options: startsAt.map((value) => ({ startsAt: value, timezone: TIMEZONE })),
      });
      setResult(created);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Der Vorschlag konnte nicht erstellt werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  function updateOption(id: string, value: string) {
    setOptions((current) =>
      current.map((option) => (option.id === id ? { ...option, value } : option)),
    );
  }

  function addOption() {
    setOptions((current) => (current.length < 6 ? [...current, createTimeDraft()] : current));
  }

  function removeOption(id: string) {
    setOptions((current) =>
      current.length === 1 ? current : current.filter((option) => option.id !== id),
    );
  }

  async function copyLink() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch {
      window.prompt("Persönlichen Link kopieren", result.link);
    }
  }

  function openEmail() {
    if (!result || !recipientEmail.trim()) return;
    window.location.href = proposalInvitationMailto({
      recipientEmail,
      recipientName: result.recipientName,
      link: result.link,
      offerTitle: result.offerTitle,
    });
  }

  function reset() {
    setRecipientName("");
    setRecipientEmail("");
    setMessage("");
    setDisplayPrice("");
    setExpiresAt(defaultProposalExpiry());
    setOptions([createTimeDraft()]);
    setResult(null);
    setCopied(false);
    setError(null);
  }

  if (result) {
    return (
      <section className="proposal-admin-result workspace-panel">
        <div className="proposal-admin-result-mark" aria-hidden="true">
          ✓
        </div>
        <p className="workspace-kicker">Persönlicher Vorschlag erstellt</p>
        <h2>Die Einladung für {result.recipientName} ist bereit.</h2>
        <p>
          {result.offerTitle} · {result.variantLabel} · {result.locationName}
        </p>
        <div className="proposal-admin-link">
          <input value={result.link} readOnly aria-label="Persönlicher Vorschlagslink" />
          <button type="button" onClick={() => void copyLink()}>
            {copied ? "Kopiert" : "Link kopieren"}
          </button>
        </div>
        <div className="proposal-admin-result-actions">
          <a href={result.link} target="_blank" rel="noreferrer">
            Vorschlag ansehen
          </a>
          {recipientEmail.trim() ? (
            <button type="button" onClick={openEmail}>
              Persönliche E-Mail öffnen
            </button>
          ) : null}
          <button type="button" className="is-secondary" onClick={reset}>
            Weiteren Vorschlag erstellen
          </button>
        </div>
        <p className="proposal-admin-help">
          Der Link ist sofort nutzbar. Erst die verbindliche Reservierung auf der Einladungsseite
          erzeugt die Buchung.
        </p>
      </section>
    );
  }

  if (!activeOffers.length) {
    return (
      <section className="workspace-panel">
        <h2>Noch kein aktives Angebot</h2>
        <p>Aktiviere zuerst ein Angebot mit mindestens einer Dauer.</p>
      </section>
    );
  }

  return (
    <section className="proposal-admin workspace-panel">
      <div className="proposal-admin-heading">
        <div>
          <p className="workspace-kicker">Persönliche Einladung</p>
          <h2>Vorschlag vorbereiten</h2>
          <p>
            Du wählst Mensch, Rahmen und konkrete Zeiten. Danach erhältst du einen fertigen
            persönlichen Link zum Prüfen und Versenden.
          </p>
        </div>
        <div className="proposal-admin-sequence" role="list" aria-label="Ablauf">
          <span role="listitem">1 · Person</span>
          <span role="listitem">2 · Rahmen</span>
          <span role="listitem">3 · Zeiten</span>
          <span role="listitem">4 · Link</span>
        </div>
      </div>

      <form className="proposal-admin-form" onSubmit={submit}>
        <fieldset>
          <legend>1. Für wen ist die Einladung?</legend>
          <div className="proposal-admin-two-columns">
            <label>
              Name
              <input
                value={recipientName}
                onChange={(event) => setRecipientName(event.target.value)}
                placeholder="Daniela Beispiel"
                required
              />
            </label>
            <label>
              E-Mail, optional
              <input
                type="email"
                value={recipientEmail}
                onChange={(event) => setRecipientEmail(event.target.value)}
                placeholder="daniela@example.de"
              />
            </label>
          </div>
          <label>
            Deine persönliche Nachricht
            <textarea
              rows={5}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Ich habe dir für unsere Begegnung zwei Zeiten ausgewählt …"
              required
            />
          </label>
        </fieldset>

        <fieldset>
          <legend>2. Welcher Rahmen ist vorgesehen?</legend>
          <div className="proposal-admin-three-columns">
            <label>
              Angebot
              <select
                value={selectedOffer?.id ?? ""}
                onChange={(event) => setOfferId(event.target.value)}
                required
              >
                {activeOffers.map((offer) => (
                  <option key={offer.id} value={offer.id}>
                    {offer.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Dauer
              <select
                value={selectedVariant?.id ?? ""}
                onChange={(event) => setVariantId(event.target.value)}
                required
              >
                {activeVariants.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.label} · {variant.durationMinutes} Min.
                  </option>
                ))}
              </select>
            </label>
            <label>
              Ort
              <select
                value={locationId}
                onChange={(event) => setLocationId(event.target.value)}
                required
              >
                {availableLocations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.mode === "online" ? "Online · Google Meet" : location.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="proposal-admin-three-columns">
            <label>
              Persönlicher Preis in €
              <input
                inputMode="decimal"
                value={displayPrice}
                onChange={(event) => setDisplayPrice(event.target.value)}
                placeholder={
                  selectedVariant?.priceCents == null
                    ? "persönlich vereinbart"
                    : `regulär ${new Intl.NumberFormat("de-DE", {
                        style: "currency",
                        currency: selectedVariant.currency,
                        maximumFractionDigits: 0,
                      }).format(selectedVariant.priceCents / 100)}`
                }
              />
              <small>Leer lassen übernimmt den regulären Preis.</small>
            </label>
            <label>
              Einladung gültig bis
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
                required
              />
            </label>
            <label>
              Bildadresse
              <input
                type="url"
                value={imageUrl}
                onChange={(event) => setImageUrl(event.target.value)}
                placeholder="https://…"
              />
              <small>Das aktuelle Portrait ist bereits eingesetzt.</small>
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend>3. Welche Zeiten möchtest du anbieten?</legend>
          <p className="proposal-admin-field-hint">
            Die Uhrzeiten werden als Berliner Zeit gespeichert – auch wenn du den Vorschlag später
            aus Kenia erstellst.
          </p>
          <div className="proposal-admin-options">
            {options.map((option, index) => (
              <div className="proposal-admin-option" key={option.id}>
                <span>{index + 1}</span>
                <label>
                  Datum und Beginn
                  <input
                    type="datetime-local"
                    value={option.value}
                    onChange={(event) => updateOption(option.id, event.target.value)}
                    required
                  />
                </label>
                <strong>{selectedVariant ? `${selectedVariant.durationMinutes} Minuten` : ""}</strong>
                <button
                  type="button"
                  onClick={() => removeOption(option.id)}
                  disabled={options.length === 1}
                  aria-label={`Zeit ${index + 1} entfernen`}
                >
                  Entfernen
                </button>
              </div>
            ))}
          </div>
          <button
            className="proposal-admin-add"
            type="button"
            onClick={addOption}
            disabled={options.length >= 6}
          >
            + Weitere Zeit
          </button>
        </fieldset>

        <div className="proposal-admin-summary">
          <div>
            <span>Vorschau</span>
            <strong>{recipientName || "Persönliche Einladung"}</strong>
            <small>
              {selectedOffer?.title} · {selectedVariant?.label} ·{" "}
              {availableLocations.find((location) => location.id === locationId)?.name ??
                "Ort wählen"}
            </small>
          </div>
          <button
            className="workspace-primary"
            type="submit"
            disabled={busy || !availableLocations.length}
          >
            {busy ? "Persönlicher Link wird erstellt …" : "Persönlichen Vorschlag erstellen"}
          </button>
        </div>

        {!availableLocations.length ? (
          <p className="workspace-notice error">
            Für dieses Angebot ist noch kein aktiver Ort zugeordnet.
          </p>
        ) : null}
        {error ? (
          <p className="workspace-notice error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </section>
  );
}
